import React, { useState } from "react";
import { WizardAnswers } from "../../types";
import Header from "../components/Header";
import WizardQuestion from "../components/WizardQuestion";
import {
  PRIMARY_GOAL_OPTIONS,
  RECEPTIVITY_OPTIONS,
  showsWhiteboard,
} from "../wizardOptions";
import styles from "./WizardView.module.css";

interface WizardViewProps {
  onAnalyse: (answers: WizardAnswers) => void;
  onClose: () => void;
  onBack: () => void;
}

export default function WizardView({ onAnalyse, onBack }: WizardViewProps) {
  const [primaryGoal, setPrimaryGoal] = useState("");
  const [receptivity, setReceptivity] = useState("");
  const [whiteboard, setWhiteboard] = useState("");

  const total = 2; // goal + receptivity are mandatory; whiteboard is optional
  const answered = Number(primaryGoal.length > 0) + Number(receptivity.length > 0);
  const ready = answered === total;
  const remaining = total - answered;
  const showWhiteboard = showsWhiteboard(primaryGoal);

  const submit = () => {
    if (!ready) return;
    onAnalyse({
      primaryGoal,
      receptivity,
      whiteboard: showWhiteboard ? whiteboard.trim() || undefined : undefined,
    });
  };

  return (
    <div className={styles.panel}>
      <Header onBack={onBack} />
      <div className={styles.body}>
        <div className={styles.content}>
          <div className={styles.sectionRow}>
            <span className={styles.sectionLabel}>Before we analyze your email</span>
            <div className={styles.divider} />
          </div>

          {ready && (
            <div className={styles.readyStrip}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>All questions answered — ready to analyze</span>
            </div>
          )}

          <div className={styles.questionsGroup}>
            <WizardQuestion
              number={1}
              question="What is the primary goal of this email?"
              placeholder="Select a goal..."
              options={PRIMARY_GOAL_OPTIONS}
              value={primaryGoal}
              onChange={setPrimaryGoal}
            />
            <WizardQuestion
              number={2}
              question="What is the prospect's current level of receptivity?"
              placeholder="Select receptivity..."
              options={RECEPTIVITY_OPTIONS}
              value={receptivity}
              onChange={setReceptivity}
            />

            {showWhiteboard && (
              <div className={styles.whiteboardCard}>
                <p className={styles.whiteboardLabel}>
                  What&rsquo;s on their whiteboard right now?
                  <span className={styles.whiteboardOptional}>optional</span>
                </p>
                <textarea
                  className={styles.whiteboardInput}
                  value={whiteboard}
                  onChange={(e) => setWhiteboard(e.target.value)}
                  placeholder="e.g. Q3 pipeline targets, a stalled renewal, a new VP ramping up..."
                  rows={3}
                />
              </div>
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <button
            className={`${styles.cta} ${ready ? styles.ctaReady : styles.ctaDisabled}`}
            onClick={submit}
            disabled={!ready}
          >
            {ready ? (
              <>
                <span className={styles.ctaIcon}>▶</span>
                <span>Analyze My Email</span>
              </>
            ) : (
              <span>Complete {remaining} question{remaining === 1 ? "" : "s"} to continue</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
